<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">Submit Data</h1>
      <p class="page-subtitle">Contribute your research data to the AlleleBC database</p>
    </div>

    <div class="submit-form">
      <div class="form-section">
        <h2 class="section-title">Data Information</h2>

        <div class="form-group">
          <label class="form-label">Data Type</label>
          <select class="form-select">
            <option>Please select data type</option>
            <option>Allele-specific Expression Data</option>
            <option>Methylation Data</option>
            <option>Genetic Variant Data</option>
            <option>Other</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">Dataset Title</label>
          <input type="text" class="form-input" placeholder="Enter dataset title">
        </div>

        <div class="form-group">
          <label class="form-label">Data Description</label>
          <textarea class="form-textarea" rows="4" placeholder="Provide detailed description of your dataset..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Research Institution</label>
          <input type="text" class="form-input" placeholder="Enter your institution name">
        </div>

        <div class="form-group">
          <label class="form-label">Contact Email</label>
          <input type="email" class="form-input" placeholder="Enter contact email address">
        </div>
      </div>

      <div class="form-section">
        <h2 class="section-title">File Upload</h2>

        <div class="upload-area">
          <div class="upload-content">
            <el-icon class="upload-icon"><Upload /></el-icon>
            <p class="upload-text">Drag files here or click to upload</p>
            <p class="upload-hint">Supported formats: CSV, TXT, JSON, BED, VCF (Max 100MB)</p>
          </div>
        </div>

        <div class="file-requirements">
          <h4>File Requirements:</h4>
          <ul>
            <li>Data must be in standard bioinformatics formats</li>
            <li>Include appropriate headers and metadata</li>
            <li>Ensure data quality and completeness</li>
            <li>Provide documentation for custom formats</li>
          </ul>
        </div>
      </div>

      <div class="form-section">
        <h2 class="section-title">Publication Information</h2>

        <div class="form-group">
          <label class="form-label">Associated Publication</label>
          <input type="text" class="form-input" placeholder="DOI or PubMed ID (if applicable)">
        </div>

        <div class="form-group">
          <label class="form-label">Data Usage License</label>
          <select class="form-select">
            <option>Select license type</option>
            <option>Creative Commons CC0</option>
            <option>Creative Commons CC BY</option>
            <option>Academic Use Only</option>
            <option>Custom License</option>
          </select>
        </div>
      </div>

      <div class="form-actions">
        <button class="submit-btn" disabled>Submit Data</button>
        <button class="cancel-btn">Cancel</button>
      </div>

      <div class="notice-section">
        <p class="notice-text">
          <strong>Note:</strong> Data submission is currently under review process.
          All submissions will be manually reviewed for quality and compliance before inclusion in the database.
          You will receive a confirmation email within 48 hours.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { Upload } from '@element-plus/icons-vue'

export default {
  name: 'DataSubmit',
  components: {
    Upload
  }
}
</script>

<style scoped>
.page-container {
  min-height: calc(100vh - 150px);
  padding: 60px 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 60px;
}

.page-title {
  font-size: 42px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
}

.page-subtitle {
  font-size: 18px;
  color: #666;
}

.submit-form {
  max-width: 800px;
  margin: 0 auto;
}

.form-section {
  background: white;
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 24px;
  margin-bottom: 30px;
  color: #2c3e50;
  font-weight: 600;
}

.form-group {
  margin-bottom: 25px;
}

.form-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: #555;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s;
  background: white;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 4px rgba(52, 152, 219, 0.1);
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.upload-area {
  border: 3px dashed #e0e0e0;
  border-radius: 15px;
  padding: 60px 20px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #3498db;
  background: rgba(52, 152, 219, 0.05);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.upload-icon {
  font-size: 48px;
  color: #3498db;
}

.upload-text {
  font-size: 18px;
  color: #2c3e50;
  margin: 0;
  font-weight: 500;
}

.upload-hint {
  color: #999;
  font-size: 14px;
  margin: 0;
}

.file-requirements {
  margin-top: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 4px solid #3498db;
}

.file-requirements h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
}

.file-requirements ul {
  margin: 0;
  padding-left: 20px;
}

.file-requirements li {
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
  line-height: 1.5;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 30px;
}

.submit-btn,
.cancel-btn {
  padding: 15px 40px;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.submit-btn {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}

.submit-btn:not(:disabled) {
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  cursor: pointer;
}

.submit-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
}

.cancel-btn {
  background: white;
  border: 2px solid #e0e0e0;
  color: #666;
}

.cancel-btn:hover {
  border-color: #999;
  color: #333;
}

.notice-section {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
}

.notice-text {
  margin: 0;
  color: #856404;
  font-size: 14px;
  line-height: 1.6;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-container {
    padding: 40px 15px;
  }

  .form-section {
    padding: 25px 20px;
  }

  .page-title {
    font-size: 32px;
  }

  .upload-area {
    padding: 40px 15px;
  }
}
</style>